import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import ReactDOM from "react-dom";
import { useEffect, useMemo, useState } from "react";
import CloseIcon from "assets/icons/close.svg?react";
import TeamIcon from "assets/icons/team.svg?react";
import workspaceService from "api/workspaceService";
import { ENotificationType, notification } from "utils/notification";
import { useAuth } from "react-oidc-context";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import { EUserType } from "dto/User/EUserType";
import { useTranslation } from "react-i18next";
import {
  TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { Checkbox, FormControlLabel } from "@mui/material";
import { JOB_GRADES, IJobGrade } from "data/job_grades";
import { getOrgChartUnits, addPersonToOrgUnit } from "components/Settings/WorkersTab/OrgChart";
import { flattenStructure, loadStructure, saveAssignment } from "components/Settings/WorkersTab/StructureEditor/treeUtils";

// ── Helpers ───────────────────────────────────────────────────────

function getAllJobGrades(): IJobGrade[] {
  try {
    const raw = localStorage.getItem("job_grades_custom_v1");
    const custom: IJobGrade[] = raw ? JSON.parse(raw) : [];
    return [...JOB_GRADES, ...custom];
  } catch {
    return [...JOB_GRADES];
  }
}

// ── Component ─────────────────────────────────────────────────────

const AddWorker: FC<Props> = ({ roles, toggle, workspaceId, refetch, initialName, initialEmail, onSuccess }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === EUserType.LegalPerson;

  const { mutate: addWorkers } = workspaceService.useInviteToWorkspace();

  const [name, setName] = useState(initialName ?? "");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [phone, setPhone] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [unitId, setUnitId] = useState("");
  const [positionName, setPositionName] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);

  const orgUnits = useMemo(() => getOrgChartUnits(), []);
  const allGrades = useMemo(() => getAllJobGrades(), []);
  const structureNodes = useMemo(() => flattenStructure(loadStructure()), []);

  const mappedRoles = useMemo(
    () =>
      roles
        .filter((role) => role.StaticType !== "owner" || isLegalPerson)
        .map((role) => ({ value: role.Id, label: role.Name })) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles]
  );

  useEffect(() => {
    if (mappedRoles.length > 0 && !selectedRole) {
      setSelectedRole(mappedRoles[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedRoles]);

  const hasOrgData = !!unitId && !!positionName && !!name;
  const canInvite = !!email && !!name && !!workspaceId;
  const canAdd = hasOrgData || (!!name && selectedNodeIds.length > 0);

  const toggleNode = (id: string) => {
    setSelectedNodeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const saveToOrgChart = (invited: boolean) => {
    if (hasOrgData) {
      addPersonToOrgUnit(unitId, {
        personFullName: name,
        positionName,
        email: email || undefined,
        phone: phone || undefined,
        gradeId: gradeId || undefined,
        memberStatus: invited ? "aktywuje" : "wpisany",
      });
    }
    if (name && selectedNodeIds.length > 0) {
      saveAssignment(name, selectedNodeIds);
    }
  };

  const handleAdd = () => {
    saveToOrgChart(false);
    onSuccess?.();
    toggle();
  };

  const handleInvite = () => {
    addWorkers(
      {
        RoleId: selectedRole,
        WorkspaceId: workspaceId,
        Email: email.replaceAll(" ", ""),
        Name: name,
      },
      {
        onSuccess: () => {
          saveToOrgChart(true);
          refetch();
          onSuccess?.();
          toggle();
        },
        onError: (e: { response?: { data?: { errorCodes?: Array<{ message: string }> } } }) => {
          notification({
            text: e.response?.data?.errorCodes?.[0]?.message ?? t("errors.somethingWentWrong"),
            title: t("errors.somethingWentWrongTitle"),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  const gradesByGroup = useMemo(() => {
    const map = new Map<string, IJobGrade[]>();
    allGrades.forEach((g) => {
      const list = map.get(g.group) ?? [];
      list.push(g);
      map.set(g.group, list);
    });
    return map;
  }, [allGrades]);

  return ReactDOM.createPortal(
    <div className={styles.modal}>
      <div className={styles.window}>
        <div className={styles.wrapper}>

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.title}>
              <TeamIcon />
              {t("settings.addWorkerTitle")}
            </div>
            <CloseIcon onClick={toggle} className={styles.icon} />
          </div>

          {/* Group 1: Dane osoby */}
          <fieldset className={styles.group}>
            <legend className={styles.groupLabel}>Dane osoby</legend>
            <div className={styles.fields}>
              <TextField
                variant="standard"
                label="Imię i Nazwisko"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />

              <div className={styles.row}>
                <TextField
                  variant="standard"
                  label="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  variant="standard"
                  label="Tel."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <FormControl variant="standard" fullWidth>
                <InputLabel>{t("forms.role")}</InputLabel>
                <Select
                  variant="standard"
                  value={selectedRole}
                  label={t("forms.role")}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={mappedRoles.length === 0}
                >
                  {mappedRoles.length === 0 && (
                    <MenuItem value="" disabled><em>Brak ról w zespole</em></MenuItem>
                  )}
                  {mappedRoles.map((r) => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </fieldset>

          {/* Group 2: Przynależność do struktury */}
          {structureNodes.length > 0 && (
            <fieldset className={styles.group}>
              <legend className={styles.groupLabel}>Przynależność do struktury</legend>
              <div className={styles.structureList}>
                {structureNodes.map((node) => (
                  <FormControlLabel
                    key={node.id}
                    style={{ marginLeft: node.depth * 16, marginBottom: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedNodeIds.includes(node.id)}
                        onChange={() => toggleNode(node.id)}
                        sx={{ padding: "3px 6px" }}
                      />
                    }
                    label={<span style={{ fontSize: 13 }}>{node.name}</span>}
                  />
                ))}
              </div>
              {structureNodes.length === 0 && (
                <div className={styles.structureEmpty}>
                  Brak struktury — najpierw zdefiniuj ją w zakładce Zespół / Struktura.
                </div>
              )}
            </fieldset>
          )}

          {/* Group 3: Przypisanie organizacyjne (stary OrgChart) */}
          <fieldset className={styles.group}>
            <legend className={styles.groupLabel}>Stanowisko (opcjonalne)</legend>
            <div className={styles.fields}>
              <FormControl variant="standard" fullWidth>
                <InputLabel>Jednostka</InputLabel>
                <Select
                  variant="standard"
                  value={unitId}
                  label="Jednostka"
                  onChange={(e) => setUnitId(e.target.value)}
                >
                  <MenuItem value=""><em>Brak przypisania</em></MenuItem>
                  {orgUnits.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <span style={{ paddingLeft: u.depth * 16 }}>{u.name}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                variant="standard"
                label="Stanowisko"
                value={positionName}
                onChange={(e) => setPositionName(e.target.value)}
                fullWidth
                disabled={!unitId}
                placeholder={unitId ? "Wpisz nazwę stanowiska" : ""}
              />

              <FormControl variant="standard" fullWidth disabled={!unitId}>
                <InputLabel>Stopień służbowy</InputLabel>
                <Select
                  variant="standard"
                  value={gradeId}
                  label="Stopień służbowy"
                  onChange={(e) => setGradeId(e.target.value)}
                  renderValue={(val) => allGrades.find((g) => g.id === val)?.name ?? ""}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {[...gradesByGroup.entries()].map(([group, grades]) => [
                    <MenuItem key={`hdr-${group}`} disabled sx={{ fontSize: 11, color: "#9e9e9e", fontWeight: 700, py: 0.4, opacity: "1 !important" }}>
                      {group}
                    </MenuItem>,
                    ...grades.map((g) => (
                      <MenuItem key={g.id} value={g.id} sx={{ pl: 3 }}>
                        {g.name}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </div>
          </fieldset>

          {/* Actions */}
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={toggle}>
              Anuluj
            </button>
            <div className={styles.actionsRight}>
              {canAdd && (
                <button className={styles.addBtn} onClick={handleAdd}>
                  Dodaj
                </button>
              )}
              <button
                className={styles.inviteBtn}
                disabled={!canInvite}
                onClick={handleInvite}
              >
                + Zaproś
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddWorker;
